import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './KatalogeVerwaltung.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const ACCEPT = '.001,.wrg,.rab,.txt';
const POLL_INTERVAL_MS = 3000;
// I1: Polling fuer einen haengenden Lauf nach 20 Minuten ohne Statuswechsel
// stoppen (Server-seitiger Gegenpart: reconcile_verwaiste_laeufe in
// app/main.py:startup_event setzt Zombie-Laeufe beim naechsten Neustart auf
// FEHLER - clientseitig soll niemand bis dahin endlos alle 3s pollen).
const POLL_TIMEOUT_MS = 20 * 60 * 1000;

interface Props { authToken: string; }

type KatalogImportStatus = 'LAEUFT' | 'FERTIG' | 'FEHLER';
type KatalogImportTyp = 'STAMM' | 'PREISE';

interface ImportLauf {
  lauf_id: number;
  partner_id: number;
  dateiname: string;
  typ: KatalogImportTyp;
  status: KatalogImportStatus;
  zaehler_neu: number;
  zaehler_geaendert: number;
  zaehler_geloescht: number;
  zaehler_preise: number;
  zaehler_uebersprungen: number;
  fehler_text: string | null;
  gestartet_am: string;
  beendet_am: string | null;
}

interface KatalogStand {
  partner_id: number;
  partner_name: string;
  anzahl_artikel: number;
  letzter_lauf: ImportLauf | null;
}

interface UploadResponse {
  lauf_id: number;
  status: KatalogImportStatus;
  dateiname: string;
}

const STATUS_LABEL: Record<KatalogImportStatus, string> = {
  LAEUFT: 'Läuft',
  FERTIG: 'Fertig',
  FEHLER: 'Fehler',
};

const STATUS_KLASSE: Record<KatalogImportStatus, string> = {
  LAEUFT: 'ktl-badge--laeuft',
  FERTIG: 'ktl-badge--fertig',
  FEHLER: 'ktl-badge--fehler',
};

const StatusBadge: React.FC<{ status: KatalogImportStatus }> = ({ status }) => (
  <span className={`ktl-badge ${STATUS_KLASSE[status]}`}>{STATUS_LABEL[status]}</span>
);

const formatDatum = (wert: string | null): string => {
  if (!wert) return '–';
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return wert;
  return d.toLocaleString('de-DE');
};

const KatalogeVerwaltung: React.FC<Props> = ({ authToken }) => {
  const [lieferanten, setLieferanten] = useState<{ partner_id: number; name: string }[]>([]);
  const [partnerId, setPartnerId] = useState<string>('');
  const [datei, setDatei] = useState<File | null>(null);
  const [laeufe, setLaeufe] = useState<ImportLauf[]>([]);
  const [stand, setStand] = useState<KatalogStand[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // I1: Zeitpunkt, seit dem ununterbrochen mindestens ein Lauf LAEUFT (fuer
  // den 20-Minuten-Timeout). I7: vorheriger LAEUFT-Zustand, um ladeStand()
  // nur beim Uebergang LAEUFT -> kein LAEUFT mehr aufzurufen.
  const laeuftSeitRef = useRef<number | null>(null);
  const vorherLaeuftRef = useRef<boolean>(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const ladeLaeufe = useCallback(async () => {
    try {
      const r = await axios.get<ImportLauf[]>(`${API_URL}/api/kataloge/laeufe`, { headers });
      setLaeufe(r.data);
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? 'Lauf-Historie konnte nicht geladen werden.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const ladeStand = useCallback(async () => {
    try {
      const r = await axios.get<KatalogStand[]>(`${API_URL}/api/kataloge/stand`, { headers });
      setStand(r.data);
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? 'Katalogstand konnte nicht geladen werden.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    axios.get(`${API_URL}/api/partners?typ=Lieferant`, { headers })
      .then((r) => setLieferanten(r.data))
      .catch(() => {
        setLieferanten([]);
        setErrorMsg('Lieferantenliste konnte nicht geladen werden.');
      });
    ladeLaeufe();
    ladeStand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const laeuftNoch = laeufe.some((l) => l.status === 'LAEUFT');

    // I7: ladeStand() nicht bei jedem Poll (Anzahl Artikel/letzter Lauf
    // aendert sich nur, wenn ein Lauf FERTIG/FEHLER wird) - nur beim
    // Uebergang "vorher lief noch etwas -> jetzt nicht mehr" nachladen. Der
    // initiale Aufruf beim Mount (oben) deckt den Erstzustand ab.
    if (vorherLaeuftRef.current && !laeuftNoch) {
      ladeStand();
    }
    vorherLaeuftRef.current = laeuftNoch;

    if (!laeuftNoch) {
      laeuftSeitRef.current = null;
      return;
    }
    if (laeuftSeitRef.current === null) {
      laeuftSeitRef.current = Date.now();
    }

    const intervalId = setInterval(() => {
      // I1: seit 20 Minuten kein Statuswechsel mehr -> Polling aufgeben
      // (haengender/verwaister Lauf, siehe POLL_TIMEOUT_MS-Kommentar oben).
      if (laeuftSeitRef.current !== null
          && Date.now() - laeuftSeitRef.current > POLL_TIMEOUT_MS) {
        clearInterval(intervalId);
        return;
      }
      ladeLaeufe();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [laeufe, ladeLaeufe, ladeStand]);

  const importStarten = async () => {
    if (!partnerId) { setErrorMsg('Bitte einen Lieferanten wählen.'); return; }
    if (!datei) { setErrorMsg('Bitte eine Datei wählen (.001, .wrg, .rab, .txt).'); return; }
    setLoading(true); setErrorMsg(null);
    try {
      const fd = new FormData();
      fd.append('datei', datei);
      await axios.post<UploadResponse>(
        `${API_URL}/api/kataloge/upload?partner_id=${partnerId}`, fd, { headers });
      setDatei(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await ladeLaeufe();
      await ladeStand();
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? 'Import konnte nicht gestartet werden.');
    } finally { setLoading(false); }
  };

  return (
    <div className="ktl-import">
      <div className="ktl-import__header">
        <h2>Kataloge</h2>
      </div>

      <div className="ktl-import__info">
        Liest komplette DATANORM-Preiskataloge der Großhändler ein (Stammdatei zuerst, danach ggf. DATPREIS mit Ihren Nettopreisen). Die Kataloge dienen als Such- und Preisbasis für Stücklisten-Import und Artikelanlage.
      </div>

      {errorMsg && <div className="ktl-fehler">{errorMsg}</div>}

      {/* Bereich 1: Upload */}
      <div className="ktl-section">
        <h3>Katalog hochladen</h3>

        <div className="ktl-field">
          <label>Lieferant</label>
          <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
            <option value="">— Lieferant wählen —</option>
            {lieferanten.map((l) => (
              <option key={l.partner_id} value={l.partner_id}>{l.name}</option>
            ))}
          </select>
        </div>

        <div className="ktl-field">
          <label>DATANORM-Datei</label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => setDatei(e.target.files?.[0] ?? null)}
          />
          <span className="ktl-hint">
            DATANORM-Stamm- oder Preisdatei (bis 250 MB)
          </span>
        </div>

        <button
          className="ktl-btn ktl-btn--primary"
          onClick={importStarten}
          disabled={loading || !datei || !partnerId}
        >
          {loading ? 'Startet…' : 'Import starten'}
        </button>
      </div>

      {/* Bereich 2: Katalogstand */}
      <div className="ktl-section">
        <h3>Katalogstand</h3>
        <div className="ktl-table-wrap">
          <table className="ktl-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Artikelanzahl</th>
                <th>Letzter Lauf</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stand.length === 0 && (
                <tr><td colSpan={4} className="ktl-table__leer">Noch keine Kataloge importiert.</td></tr>
              )}
              {stand.map((s) => (
                <tr key={s.partner_id}>
                  <td>{s.partner_name}</td>
                  <td>{s.anzahl_artikel}</td>
                  <td>{s.letzter_lauf ? formatDatum(s.letzter_lauf.beendet_am ?? s.letzter_lauf.gestartet_am) : '–'}</td>
                  <td>{s.letzter_lauf ? <StatusBadge status={s.letzter_lauf.status} /> : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bereich 3: Lauf-Historie */}
      <div className="ktl-section">
        <h3>Import-Läufe</h3>
        <div className="ktl-table-wrap">
          <table className="ktl-table">
            <thead>
              <tr>
                <th>Datei</th>
                <th>Typ</th>
                <th>Status</th>
                <th>Neu</th>
                <th>Geändert</th>
                <th>Gelöscht</th>
                <th>Preise</th>
                <th>Übersprungen</th>
                <th>Gestartet</th>
                <th>Beendet</th>
              </tr>
            </thead>
            <tbody>
              {laeufe.length === 0 && (
                <tr><td colSpan={10} className="ktl-table__leer">Noch keine Import-Läufe vorhanden.</td></tr>
              )}
              {laeufe.map((l) => (
                <tr key={l.lauf_id} title={l.fehler_text ?? undefined}>
                  <td>{l.dateiname}</td>
                  <td>{l.typ}</td>
                  <td><StatusBadge status={l.status} /></td>
                  <td>{l.zaehler_neu}</td>
                  <td>{l.zaehler_geaendert}</td>
                  <td>{l.zaehler_geloescht}</td>
                  <td>{l.zaehler_preise}</td>
                  <td>{l.zaehler_uebersprungen}</td>
                  <td>{formatDatum(l.gestartet_am)}</td>
                  <td>{formatDatum(l.beendet_am)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KatalogeVerwaltung;
